import { AviaFilterStorageService } from '../../services/router/avia-filter-storage.service';
import {ChangeDetectionStrategy, ChangeDetectorRef, Component, HostListener, OnDestroy, OnInit} from "@angular/core";
import {AviaCardComponent} from "./avia-card/avia-card.component";
import {BodyClassDirective} from "../../directives/body-class.directive";
import {ActivatedRoute, Router} from "@angular/router";
import {RouterRequestService} from "../../services/router/requests/avia-request.service";
import {AviaSearchService} from "../../services/router/avia-search.service";
import {
  AviaSearchAppliedFiltersRequest,
  AviaResultItineraries,
  AviaSearchResponse,
  AviaSearchResult
} from "../../services/router/interfaces/avia-search";
import {switchMap, takeWhile, tap} from "rxjs/operators";
import {concat, interval, of, Subject, Subscription} from "rxjs";
import {SkeletonCardComponent} from "./avia-card/avia-card-skeleton/avia-card-skeleton.component";
import {SearchProgressLinearComponent} from "../../components/search-progress-linear/search-progress-linear.component";
import {SearchProgressService} from "../../services/search-progress.service";
import {AviaNotFoundComponent} from "./avia-not-found/avia-not-found.component";
import {WindowRefService} from "../../services/window-ref.service";
import {PriceFormatPipe} from "../../services/router/pipes/price-format.pipe";
import {Title} from "@angular/platform-browser";
import {BackBtnService} from "../../services/router/back-btn.service";
import {AviaFiltersComponent} from "./avia-filters/avia-filters.component";

@Component({
  selector: 'page-avia',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './avia.component.mobile.html',
  styleUrl: './avia.component.mobile.scss',
  imports: [
    AviaCardComponent,
    BodyClassDirective,
    SearchProgressLinearComponent,
    AviaNotFoundComponent,
    SkeletonCardComponent,
    AviaFiltersComponent,
  ],
  standalone: true
})
export class PageAviaComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private baseTitle = '';
  private searchSubscription: Subscription = null;
  private pageSubscription: Subscription = null;
  private currentFiltersRequest: AviaSearchAppliedFiltersRequest | null = null;
  private currentPage = 1;
  private totalCount = 0;
  private isSearchCompleted = false;
  private isNextPageLoading = false;
  private readonly nextPageScrollOffset = 500;
  searchId?: string;
  restoredFiltersRequest: AviaSearchAppliedFiltersRequest | null = null;
  private filtersStorageKey = '';
  constructor(
    private filterStorage: AviaFilterStorageService,
    private route: ActivatedRoute,
    private requestService: RouterRequestService,
    private searchService: AviaSearchService,
    private cdr: ChangeDetectorRef,
    private searchProgressService: SearchProgressService,
    private windowRef: WindowRefService,
    private priceFormat: PriceFormatPipe,
    private titleService: Title,
    private backBtnService: BackBtnService,
  ) {
  }

  aviaItineraries: AviaResultItineraries[];
  isLoading: boolean = false;
  isShowErrors: boolean = false;
  hasFirstResults: boolean = false;

  get isEmptyFilteredResults(): boolean {
    return this.isSearchCompleted
      && !this.aviaItineraries?.length
      && Object.keys(this.currentFiltersRequest?.filters || {}).length > 0;
  }

  ngOnInit(): void {
    this.backBtnService.setBackUrl('aviaListing');
    this.windowRef.nativeWindow.brxTracking.sendAnalyticsEvent('flights_listing_open', this.route.snapshot.queryParams);
    this.baseTitle = this.titleService.getTitle() || '';
    this.startAviaSearch();
  }

  ngOnDestroy(): void {
    this.unsubscribeSearch();
    this.unsubscribeNextPage();
    this.destroy$.next();
    this.destroy$.complete();
  }

  @HostListener('window:scroll')
  onWindowScroll() {
    if (this.shouldLoadNextPage()) {
      this.loadNextPage();
    }
  }

  startAviaSearch() {
    const queryParams = this.route.snapshot.queryParams;
    const request = this.searchService.createSearchRequest(queryParams);
    this.filtersStorageKey = this.filterStorage.createKey(request.params, this.windowRef.nativeWindow.app?.currency || '');
    this.restoredFiltersRequest = this.filterStorage.load(this.filtersStorageKey);
    this.currentFiltersRequest = this.restoredFiltersRequest;

    this.unsubscribeSearch();
    this.resetPaginationState();
    this.searchSubscription = this.requestService.startSearch(request)
      .pipe(
        switchMap((response: AviaSearchResponse) => {
          this.searchId = response.id;

          this.isLoading = true;
          this.searchProgressService.start(); // Стартуем прогрессбар
          this.cdr.detectChanges();  // Обновляем интерфейс сразу, чтобы пользователь увидел прогресс

          // Первый запрос выполняем сразу
          return this.requestService.getAvia(response.id, this.currentFiltersRequest || {}).pipe(
            tap((result: AviaSearchResult) => {
              // Обрабатываем первый ответ сразу
              if (result.results?.itineraries?.length && this.aviaItineraries !== result.results.itineraries) {
                this.applyAviaResult(result);
              }
            }),
            // Далее выполняем интервальные запросы
            switchMap(() => {
              const startTime = Date.now();
              const timeoutMs = response.timeout * 1000;

              return interval(2000).pipe(
                switchMap(() => this.requestService.getAvia(response.id, this.currentFiltersRequest || {})), // Запрос данных каждые 2 секунды
                tap((result: AviaSearchResult) => {
                  // Как только пришли результаты — сразу обновляем интерфейс
                  if (result.results?.itineraries?.length && this.aviaItineraries !== result.results.itineraries) {
                    this.applyAviaResult(result);
                  }
                }),
                // Завершаем, если истекло время или статус стал 'completed'
                takeWhile((result: AviaSearchResult) => {
                  const isTimeout = Date.now() - startTime >= timeoutMs;
                  const isCompleted = result.status === 'completed';
                  return !isTimeout && !isCompleted;
                }, true)  // keep checking until timeout or completed
              );
            })
          );
        })
      )
      .subscribe({
        next: (result: AviaSearchResult) => {
          console.log('📝 Итерация опроса', result);
          this.isSearchCompleted = result.status === 'completed';

          if (result.results?.itineraries?.length) {
            if (!this.hasFirstResults) {
              this.windowRef.nativeWindow.brxTracking.sendAnalyticsEvent(
                'flights_listing_success',
                { ...this.route.snapshot.queryParams }
              );
              this.hasFirstResults = true;
              this.isLoading = false;
            }

            this.applyAviaResult(result);
            this.cdr.detectChanges();  // Обновляем интерфейс
          }
        },
        error: (err) => {
          this.windowRef.nativeWindow.brxTracking.sendAnalyticsEvent(
            'flights_listing_error',
            this.route.snapshot.queryParams
          );
          this.isShowErrors = true;
          this.cdr.detectChanges();  // Обновляем интерфейс
          console.error('🔥 Ошибка при поиске:', err);
        },
        complete: () => {
          this.isLoading = false;
          this.searchProgressService.end(); // Стоп прогрессбара

          if (!this.aviaItineraries?.length) {
            this.isShowErrors = true;
          }

          this.cdr.detectChanges();  // Обновляем интерфейс
          console.log('✅ Поиск завершён');
        }
      });
  }

  applyAviaFilters(request: AviaSearchAppliedFiltersRequest) {
    if (!this.searchId) {
      return;
    }

    const searchId = this.searchId;

    this.unsubscribeSearch();
    this.unsubscribeNextPage();
    this.resetPaginationState();
    this.currentFiltersRequest = request;
    this.filterStorage.save(this.filtersStorageKey, request);
    this.isLoading = true;
    this.isShowErrors = false;
    this.aviaItineraries = [];
    this.searchProgressService.start();
    this.cdr.detectChanges();
    this.scrollToTop();

    const startTime = Date.now();
    const timeoutMs = 30000;

    this.searchSubscription = this.requestService.getAvia(searchId, request)
      .pipe(
        switchMap((firstResult: AviaSearchResult) => {
          if (firstResult.status === 'completed') {
            return of(firstResult);
          }

          return concat(
            of(firstResult),
            interval(2000).pipe(
              switchMap(() => this.requestService.getAvia(searchId, request)),
              takeWhile((result: AviaSearchResult) => {
                const isTimeout = Date.now() - startTime >= timeoutMs;
                const isCompleted = result.status === 'completed';

                return !isTimeout && !isCompleted;
              }, true)
            )
          );
        })
      )
      .subscribe({
        next: (result: AviaSearchResult) => {
          this.applyAviaResult(result);
        },
        error: (err) => {
          this.windowRef.nativeWindow.brxTracking.sendAnalyticsEvent(
            'flights_listing_error',
            this.route.snapshot.queryParams
          );
          this.isShowErrors = true;
          this.searchProgressService.end();
          this.cdr.detectChanges();
          console.error('🔥 Ошибка при поиске:', err);
        },
        complete: () => {
          this.isLoading = false;
          this.searchProgressService.end();

          if (!this.aviaItineraries?.length) {
            this.isShowErrors = true;
          }

          this.cdr.detectChanges();
        }
      });
  }

  get lowestPrice(): number {
    if (!this.aviaItineraries?.length) {
      return 0;
    }
    return Math.min(...this.aviaItineraries.map(it => it.price.totalPrice.amount));
  }

  isBestPrice(itinerary: any): boolean {
    // Найдем первую карточку с минимальной ценой
    const minPriceItinerary = this.aviaItineraries.find(
      it => it.price.totalPrice.amount === this.lowestPrice
    );

    // Устанавливаем isBestPrice только для первой карточки с минимальной ценой
    return minPriceItinerary === itinerary;
  }

  setTitlePrice() {
    if (!this.aviaItineraries?.length) return;

    const price = this.aviaItineraries[0].price.totalPrice.amount;
    const currency = this.aviaItineraries[0].price.totalPrice.currency_sign || '';
    const formattedPrice = this.priceFormat.transform(price) ?? String(price);
    const priceText = `${formattedPrice} ${currency}`.trim();
    const baseTitle = this.baseTitle || this.titleService.getTitle() || '';
    const newTitle = baseTitle ? `${baseTitle} | ${priceText}` : priceText;

    this.titleService.setTitle(newTitle);
  }

  private applyAviaResult(result: AviaSearchResult) {
    const itineraries = result.results?.itineraries || [];
    this.aviaItineraries = itineraries;
    this.totalCount = result.totalCount || itineraries.length;
    this.isSearchCompleted = result.status === 'completed';

    if (itineraries.length) {
      this.isLoading = false;
      this.isShowErrors = false;
      this.setTitlePrice();
    }

    this.cdr.detectChanges();
    this.scheduleNextPageCheck();
  }

  private appendAviaResult(result: AviaSearchResult) {
    const itineraries = result.results?.itineraries || [];
    const existingIds = new Set((this.aviaItineraries || []).map((itinerary) => itinerary.id));
    const newItineraries = itineraries.filter((itinerary) => !existingIds.has(itinerary.id));

    this.totalCount = result.totalCount || this.totalCount;
    this.isSearchCompleted = result.status === 'completed';

    if (!newItineraries.length) {
      this.totalCount = this.aviaItineraries?.length || 0;
      this.cdr.detectChanges();
      return;
    }

    this.aviaItineraries = [
      ...(this.aviaItineraries || []),
      ...newItineraries,
    ];

    this.cdr.detectChanges();
    this.scheduleNextPageCheck();
  }

  private scheduleNextPageCheck() {
    this.windowRef.nativeWindow.setTimeout(() => {
      if (this.shouldLoadNextPage()) {
        this.loadNextPage();
      }
    }, 0);
  }

  private shouldLoadNextPage(): boolean {
    const loadedCount = this.aviaItineraries?.length || 0;

    if (
      !this.searchId
      || !this.isSearchCompleted
      || this.isLoading
      || this.isNextPageLoading
      || loadedCount >= this.totalCount
    ) {
      return false;
    }

    const window = this.windowRef.nativeWindow;
    const documentElement = this.windowRef.document.documentElement;

    return window.scrollY + window.innerHeight >= documentElement.scrollHeight - this.nextPageScrollOffset;
  }

  private loadNextPage() {
    if (!this.searchId) {
      return;
    }

    const nextPage = this.currentPage + 1;
    const request = this.buildNextPageRequest(nextPage);

    this.isNextPageLoading = true;
    this.pageSubscription = this.requestService.getAvia(this.searchId, request)
      .subscribe({
        next: (result: AviaSearchResult) => {
          this.currentPage = nextPage;
          this.appendAviaResult(result);
        },
        error: (err) => {
          this.isNextPageLoading = false;
          this.pageSubscription = null;
          console.error('🔥 Ошибка при загрузке следующей страницы авиа:', err);
          this.cdr.detectChanges();
        },
        complete: () => {
          this.isNextPageLoading = false;
          this.pageSubscription = null;
          this.cdr.detectChanges();
        }
      });
  }

  private buildNextPageRequest(page: number): AviaSearchAppliedFiltersRequest {
    return {
      filters: this.currentFiltersRequest?.filters || {},
      sortBy: this.currentFiltersRequest?.sortBy,
      page,
    };
  }

  private resetPaginationState() {
    this.unsubscribeNextPage();
    this.currentPage = 1;
    this.totalCount = 0;
    this.isSearchCompleted = false;
    this.isNextPageLoading = false;
  }

  private scrollToTop() {
    this.windowRef.nativeWindow.setTimeout(() => {
      this.windowRef.nativeWindow.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }, 100);
  }

  private unsubscribeSearch() {
    if (!this.searchSubscription) {
      return;
    }

    this.searchSubscription.unsubscribe();
    this.searchSubscription = null;
  }

  private unsubscribeNextPage() {
    if (!this.pageSubscription) {
      return;
    }

    this.pageSubscription.unsubscribe();
    this.pageSubscription = null;
  }
}
